import React, { ClipboardEvent, useContext, useEffect, useState } from "react";
import {
  BACKGROUND_GRAY,
  GREEN_05,
  LIGHT_BACKGROUND_GRAY,
} from "../../../constants/css.constants";
import BaseTable from "../../../components/BaseTable/BaseTable";
import ClickableCell from "../../../components/ClickableCell/ClickableCell";
import TableContainer from "../../../components/TableContainer/TableContainer";
import { formatNumbersWithCommas } from "../../../constants/utils";
import { COPY_PASTE_SPLIT } from "../../../constants/app.constants";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import {
  DISTRIBUTION_COLUMNS,
  DistributionContext,
} from "../../../contexts/DistributionContext";
import { FuturesContext } from "../../../contexts/FuturesContext";
import { ShipmentHeaderContext } from "../../../contexts/ShipmentHeaderContext";
import useEditRights from "../../../hooks/useEditRights";

const DRS_PERCENT_ROW = 0;
const DRS_ROW = 1;
const DC_ROW = 2;
const FUTURES_TOTAL_ROW = 3;
const DistributionTable = () => {
  const { horizontalHeaders, monthsInFirstTwoSeasons } = useContext(
    ShipmentHeaderContext,
  );
  const { distributionData, setDistributionData, setNextCell, selectedCell } =
    useContext(DistributionContext);
  const { futuresTotals } = useContext(FuturesContext);

  const { tab } = useParams<{ tab: string }>();
  const { pathname } = useLocation();

  const { canEdit } = useEditRights();
  // Will come in handy when it comes time to call back end
  const [currentLocation, setCurrentLocation] = useState("");

  useEffect(() => {
    const DRS = futuresTotals.map((val, i) =>
        boundDrsValue(Number(distributionData[DRS_ROW][i]), i),
    );
    DRS.push(DRS.reduce((acc, val) => Number(acc) + Number(val), 0));
    const DC: number[] = futuresTotals.map((val, i) => val - Number(DRS[i]));
    DC.push(DC.reduce((acc, val) => acc + val, 0));

    const DRS_PERCENT: number[] = futuresTotals.map((val, i) =>
        val === 0 ? 0 : calculateDrsPercent(Number(DRS[i]), i));
    DRS_PERCENT.push(-1)

    const futuresTotalsDistributionMethodTable = futuresTotals.slice();
    futuresTotalsDistributionMethodTable.push(futuresTotalsDistributionMethodTable.reduce((acc, val) => acc + val, 0));
    setDistributionData([DRS_PERCENT, DRS, DC, futuresTotalsDistributionMethodTable]);
    setCurrentLocation(pathname);
  }, [futuresTotals, currentLocation]);


  //Formula for DRS% = (DRS/Futures Total) * 100
  const calculateDrsPercent = (valDRS: number, column: number) =>
      futuresTotals[column] === 0 ? 0 :
        (boundDrsValue(valDRS, column) / futuresTotals[column]) * 100;

  //Formula for DC = Futures Total - DRS
  const calculateDC = (valDRS: number, column: number) =>
    futuresTotals[column] - valDRS;

  const boundDrsValue = (valDRS: number, column: number) =>
      valDRS < 0 ? 0 :
          valDRS > futuresTotals[column] ? futuresTotals[column] :
              valDRS

  // Only the DRS row is available to change
  const updateDistributionTable = (column: number) => (value: string) => {
    const updatedRowDRS = distributionData[DRS_ROW].slice(0, column)
      .concat([ boundDrsValue(Number(value), column) ])
      .concat(distributionData[DRS_ROW].slice(column + 1));

    const DRSPercentRow: Array<number> =
      (distributionData[DRS_PERCENT_ROW] as number[])
            .slice(0, column)
            .concat([calculateDrsPercent(updatedRowDRS[column] as number, column)])
            .concat((distributionData[DRS_PERCENT_ROW] as number[]).slice(column + 1))

    updatedRowDRS[updatedRowDRS.length - 1] = updatedRowDRS.slice(0, updatedRowDRS.length - 1).reduce(
      (acc: number, val: number) => acc + val,
      0,
    );
    const DC: number[] =
      (distributionData[DC_ROW] as number[])
            .slice(0, column)
            .concat(futuresTotals[column] - Number(updatedRowDRS[column]))
            .concat((distributionData[DC_ROW] as number[]).slice(column + 1))

    DC[DC.length - 1] = DC.slice(0, DC.length - 1).reduce(
      (acc: number, val: number) => acc + val,
      0,
    );

    let futuresTotalsDistributionMethodTableSum: number = 0;
    const futuresTotalsDistributionMethodTable = new Array<number>(futuresTotals.length);
    for (let i=0; i<futuresTotals.length; i++){
        futuresTotalsDistributionMethodTable[i] = Number(updatedRowDRS[i]) + Number(DC[i]);
        if (futuresTotalsDistributionMethodTable[i] != futuresTotals[i]){
            //debug safety check
            console.log("APLA ERROR: Future Totals don't match after Distribution Method Table edit")
        }
        futuresTotalsDistributionMethodTableSum += futuresTotalsDistributionMethodTable[i];
    }
    futuresTotalsDistributionMethodTable.push(futuresTotalsDistributionMethodTableSum);
    const updatedData = [DRSPercentRow, updatedRowDRS, DC, futuresTotalsDistributionMethodTable];
    setDistributionData(updatedData);
  };

  // If user pastes multiple rows, nixing any extras
  const onPaste =
    (column: number) => (e: ClipboardEvent<HTMLTableCellElement>) => {
      const data = e.clipboardData.getData("text");
      const splitData = data
        .split(COPY_PASTE_SPLIT)
        .map((datum) =>
          datum
            .split(/\t/)
            .map((val) =>
              parseFloat(val.replaceAll(",", "").replaceAll("%", "")),
            ),
        )[0];
      const DRS_PERCENT: Array<number> = (distributionData[DRS_PERCENT_ROW] as number[])
        .slice(0, column)
        .concat(splitData.map((val, i) => calculateDrsPercent(val, i + column)))
        .concat(
          (distributionData[DRS_PERCENT_ROW] as number[]).slice(
            column + splitData.length,
          ),
        );

      const DRS = distributionData[DRS_ROW].slice(0, column)
          .concat( splitData.map( (val, i) => boundDrsValue(val, i + column) ))
          .concat(
              distributionData[DRS_ROW].slice(column + splitData.length),
          );
      DRS[DRS.length - 1] = DRS.slice(0, DRS.length - 1).reduce(
        (acc: number, val: number) => acc + val,
        0,
      );

      const DC: Array<string | number> = distributionData[DC_ROW].slice(
        0,
        column,
      )
        .concat(
          splitData.map(
            (val, i) => futuresTotals[column + i] - Number(DRS[column + i]),
          ),
        )
        .concat(distributionData[DC_ROW].slice(column + splitData.length));

      DC[DC.length - 1] = DC.slice(0, DC.length - 1).reduce(
        (acc: number, val: number) => acc + val,
        0,
      );

        let futuresTotalsDistributionMethodTableSum: number = 0;
        const futuresTotalsDistributionMethodTable = new Array<number>(futuresTotals.length);
        for (let i=0; i<futuresTotals.length; i++){
            futuresTotalsDistributionMethodTable[i] = Number(DRS[i]) + Number(DC[i]);
            if (futuresTotalsDistributionMethodTable[i] != futuresTotals[i]){
                //debug safety check
                console.log("APLA ERROR: Future Totals don't match after Distribution Method Table edit")
            }
            futuresTotalsDistributionMethodTableSum += futuresTotalsDistributionMethodTable[i];
        }
        futuresTotalsDistributionMethodTable.push(futuresTotalsDistributionMethodTableSum);

      const finalDistributionData = [DRS_PERCENT, DRS, DC, futuresTotalsDistributionMethodTable];
      setDistributionData(finalDistributionData);
    };

  const isTB = tab === "tb";
  const isDisabled = isTB || !canEdit;
  const innerCell = (value: number, column: number, row: number) => (
    <ClickableCell
      disabled={
        isDisabled ||
        column === horizontalHeaders.MONTHS.length ||  // last column Total
          row === DRS_PERCENT_ROW ||
          row > DRS_ROW
      }
      updateData={updateDistributionTable(column)}
      transform={(value) =>
        horizontalHeaders.MONTHS.length === column &&
        row === DRS_PERCENT_ROW  // 0
          ? "-"
          : row === DRS_PERCENT_ROW  // 0
            ? `${Number(value).toFixed(2)}%`
            : formatNumbersWithCommas(value)
      }
      value={value}
      onPaste={onPaste(column)}
      styles={
        isDisabled && column < monthsInFirstTwoSeasons
          ? {
              backgroundColor: LIGHT_BACKGROUND_GRAY,
              color: "#000",
              cursor: "default",
            }
          : isDisabled ||
              horizontalHeaders.MONTHS.length === column ||
              ((row === DRS_PERCENT_ROW || row === DC_ROW) && column >= monthsInFirstTwoSeasons) ||
              row === FUTURES_TOTAL_ROW
            ? { backgroundColor: BACKGROUND_GRAY, cursor: "default" }
            : column < monthsInFirstTwoSeasons
              ? { cursor: "default", backgroundColor: GREEN_05, color: "black" }
              : { backgroundColor: "#fff", color: "black" }
      }
      type={"number"}
      setNextCell={setNextCell(row, column)}
      isSelected={selectedCell.row === row && selectedCell.column === column}
    />
  );

  return (
    <>
      <TableContainer label={"Distribution Method"}>
        <BaseTable
          data={distributionData}
          horizontalHeaders={[...horizontalHeaders.MONTHS, "Total"]}
          tab={tab.toUpperCase()}
          verticalHeaders={DISTRIBUTION_COLUMNS}
          innerCell={innerCell}
        />
      </TableContainer>
    </>
  );
};

export default DistributionTable;
